import type { EvaluationPlanCategoryScope } from './evaluationPlanCategorySelection';
import type { EvaluationPlanRowScope } from '../types/evaluationPlan';
import {
  getWorkspaceEvaluationPlanApplied,
  type WorkspaceEvaluationPlanAppliedSnapshot,
} from '../services/workspaceEvaluationPlanAppliedService';
import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import { deviceCacheStorage } from './deviceCacheStorage';

const APPLIED_CACHE_VERSION = 'v3';
const APPLIED_SNAPSHOTS_CACHE_VERSION = 'v1';

/** 세션 내 즉시 재사용(같은 클래스 피커·등록 페이지 왕복) */
const appliedSnapshotsMemory = new Map<string, WorkspaceEvaluationPlanAppliedSnapshot[]>();
const appliedSnapshotsInflight = new Map<string, Promise<WorkspaceEvaluationPlanAppliedSnapshot[]>>();

export type WorkspaceEvaluationPlanAppliedEntry = {
  planId: string;
  categoryName: string;
  categoryScope: EvaluationPlanCategoryScope;
  /** 개별 평가계획 출처(좌측 색상). 적용본 수정 시 user. */
  planScope: EvaluationPlanRowScope;
};

function appliedStorageKey(workspaceId: string, gradeId: string): string {
  return `cp.workspaceEvaluationPlan.applied.${APPLIED_CACHE_VERSION}.${workspaceId.trim()}.${gradeId.trim()}`;
}

function legacyAppliedStorageKeyV2(workspaceId: string, gradeId: string): string {
  return `cp.workspaceEvaluationPlan.applied.v2.${workspaceId.trim()}.${gradeId.trim()}`;
}

function legacyAppliedStorageKey(workspaceId: string, gradeId: string, categoryName: string): string {
  return `cp.workspaceEvaluationPlan.applied.v1.${workspaceId.trim()}.${gradeId.trim()}.${encodeURIComponent(categoryName.trim())}`;
}

function normalizePlanScope(raw: unknown, fallback: EvaluationPlanRowScope = 'user'): EvaluationPlanRowScope {
  return raw === 'admin' || raw === 'team' || raw === 'user' ? raw : fallback;
}

function normalizeEntries(raw: unknown): WorkspaceEvaluationPlanAppliedEntry[] {
  if (!raw || typeof raw !== 'object') return [];
  const ids = (raw as { entries?: unknown; ids?: unknown }).entries ?? (raw as { ids?: unknown }).ids;
  if (!Array.isArray(ids)) return [];
  const out: WorkspaceEvaluationPlanAppliedEntry[] = [];
  const seen = new Set<string>();
  for (const item of ids) {
    if (typeof item === 'string') {
      const planId = item.trim();
      if (!planId || seen.has(planId)) continue;
      seen.add(planId);
      out.push({ planId, categoryName: '', categoryScope: 'user', planScope: 'user' });
      continue;
    }
    if (!item || typeof item !== 'object') continue;
    const row = item as Partial<WorkspaceEvaluationPlanAppliedEntry>;
    const planId = typeof row.planId === 'string' ? row.planId.trim() : '';
    const categoryName = typeof row.categoryName === 'string' ? row.categoryName.trim() : '';
    const categoryScope =
      row.categoryScope === 'admin' || row.categoryScope === 'team' || row.categoryScope === 'user'
        ? row.categoryScope
        : 'user';
    const planScope = normalizePlanScope(row.planScope, categoryScope);
    if (!planId || seen.has(planId)) continue;
    seen.add(planId);
    out.push({ planId, categoryName, categoryScope, planScope });
  }
  return out;
}

function readLegacyCategoryIds(workspaceId: string, gradeId: string, categoryName: string): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = deviceCacheStorage.getItem(legacyAppliedStorageKey(workspaceId, gradeId, categoryName));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { ids?: unknown } | unknown;
    const ids = Array.isArray(parsed)
      ? parsed
      : parsed && typeof parsed === 'object' && Array.isArray((parsed as { ids?: unknown }).ids)
        ? (parsed as { ids: unknown[] }).ids
        : [];
    return ids.filter((id): id is string => typeof id === 'string' && id.trim().length > 0).map((id) => id.trim());
  } catch {
    return [];
  }
}

export function loadWorkspaceEvaluationPlanAppliedEntries(
  workspaceId: string,
  gradeId: string,
  knownCategories?: Array<{ name: string; scope: EvaluationPlanCategoryScope }>
): WorkspaceEvaluationPlanAppliedEntry[] {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  if (!ws || !gid || typeof window === 'undefined') return [];
  try {
    const raw = deviceCacheStorage.getItem(appliedStorageKey(ws, gid));
    let entries = raw ? normalizeEntries(JSON.parse(raw)) : [];

    if (!entries.length) {
      const legacyV2 = deviceCacheStorage.getItem(legacyAppliedStorageKeyV2(ws, gid));
      if (legacyV2) {
        entries = normalizeEntries(JSON.parse(legacyV2));
      }
    }

    if (knownCategories?.length) {
      const byId = new Map(entries.map((e) => [e.planId, e]));
      for (const cat of knownCategories) {
        const name = cat.name.trim();
        if (!name) continue;
        for (const planId of readLegacyCategoryIds(ws, gid, name)) {
          if (byId.has(planId)) continue;
          const entry: WorkspaceEvaluationPlanAppliedEntry = {
            planId,
            categoryName: name,
            categoryScope: cat.scope,
            planScope: cat.scope,
          };
          byId.set(planId, entry);
          entries.push(entry);
        }
      }
      if (entries.length) {
        saveWorkspaceEvaluationPlanAppliedEntries(ws, gid, entries);
      }
    }

    return entries;
  } catch {
    return [];
  }
}

export function saveWorkspaceEvaluationPlanAppliedEntries(
  workspaceId: string,
  gradeId: string,
  entries: Iterable<WorkspaceEvaluationPlanAppliedEntry>
): void {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  if (!ws || !gid || typeof window === 'undefined') return;
  try {
    const seen = new Set<string>();
    const list: WorkspaceEvaluationPlanAppliedEntry[] = [];
    for (const entry of Array.from(entries)) {
      const planId = entry.planId.trim();
      const categoryName = entry.categoryName.trim();
      if (!planId || seen.has(planId)) continue;
      seen.add(planId);
      const categoryScope =
        entry.categoryScope === 'admin' || entry.categoryScope === 'team' || entry.categoryScope === 'user'
          ? entry.categoryScope
          : 'user';
      list.push({
        planId,
        categoryName,
        categoryScope,
        planScope: normalizePlanScope(entry.planScope, categoryScope),
      });
    }
    deviceCacheStorage.setItem(
      appliedStorageKey(ws, gid),
      JSON.stringify({ entries: list, savedAt: Date.now() })
    );
  } catch {
    /* quota */
  }
}

function appliedSnapshotsStorageKey(workspaceId: string, gradeId: string): string {
  return `cp.workspaceEvaluationPlan.appliedSnapshots.${APPLIED_SNAPSHOTS_CACHE_VERSION}.${workspaceId.trim()}.${gradeId.trim()}`;
}

function appliedSnapshotsMemoryKey(workspaceId: string, gradeId: string): string {
  return `${workspaceId.trim()}\0${gradeId.trim()}`;
}

function isAppliedSnapshotShape(raw: unknown): raw is WorkspaceEvaluationPlanAppliedSnapshot {
  if (!raw || typeof raw !== 'object') return false;
  const row = raw as Partial<WorkspaceEvaluationPlanAppliedSnapshot>;
  return (
    typeof row.id === 'string' &&
    !!row.id.trim() &&
    !!row.plan &&
    typeof row.plan === 'object' &&
    Array.isArray(row.standards)
  );
}

function normalizeAppliedSnapshots(raw: unknown): WorkspaceEvaluationPlanAppliedSnapshot[] | null {
  if (!raw || typeof raw !== 'object') return null;
  const list = (raw as { snapshots?: unknown }).snapshots;
  if (!Array.isArray(list)) return null;
  const out: WorkspaceEvaluationPlanAppliedSnapshot[] = [];
  for (const item of list) {
    if (!isAppliedSnapshotShape(item)) continue;
    out.push(item);
  }
  /** 빈 배열도 유효 캐시(적용 0건)로 취급 — 호출부에서 found 여부 구분 */
  return out;
}

/**
 * 클래스 적용 평가계획 전체 스냅샷 캐시.
 * 등록 페이지·평가셀 가져오기 모달이 공유한다.
 */
export function loadWorkspaceEvaluationPlanAppliedSnapshots(
  workspaceId: string,
  gradeId: string
): { found: boolean; snapshots: WorkspaceEvaluationPlanAppliedSnapshot[] } {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  if (!ws || !gid) return { found: false, snapshots: [] };

  const memKey = appliedSnapshotsMemoryKey(ws, gid);
  const mem = appliedSnapshotsMemory.get(memKey);
  if (mem) return { found: true, snapshots: mem };

  if (typeof window === 'undefined') return { found: false, snapshots: [] };
  try {
    const raw = deviceCacheStorage.getItem(appliedSnapshotsStorageKey(ws, gid));
    if (!raw) return { found: false, snapshots: [] };
    const snapshots = normalizeAppliedSnapshots(JSON.parse(raw));
    if (!snapshots) return { found: false, snapshots: [] };
    appliedSnapshotsMemory.set(memKey, snapshots);
    return { found: true, snapshots };
  } catch {
    return { found: false, snapshots: [] };
  }
}

export function saveWorkspaceEvaluationPlanAppliedSnapshots(
  workspaceId: string,
  gradeId: string,
  snapshots: WorkspaceEvaluationPlanAppliedSnapshot[],
  options?: { force?: boolean }
): void {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  if (!ws || !gid) return;
  const list = Array.isArray(snapshots) ? snapshots.slice() : [];
  appliedSnapshotsMemory.set(appliedSnapshotsMemoryKey(ws, gid), list);
  if (typeof window === 'undefined') return;
  if (!options?.force && !canWriteSchoolScopedDataCache(ws)) return;
  try {
    deviceCacheStorage.setItem(
      appliedSnapshotsStorageKey(ws, gid),
      JSON.stringify({ snapshots: list, savedAt: Date.now() })
    );
  } catch {
    /* quota — 메모리 캐시는 유지 */
  }
}

export function clearWorkspaceEvaluationPlanAppliedSnapshots(workspaceId: string, gradeId: string): void {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  if (!ws || !gid) return;
  appliedSnapshotsMemory.delete(appliedSnapshotsMemoryKey(ws, gid));
  appliedSnapshotsInflight.delete(appliedSnapshotsMemoryKey(ws, gid));
  if (typeof window === 'undefined') return;
  try {
    deviceCacheStorage.removeItem(appliedSnapshotsStorageKey(ws, gid));
  } catch {
    /* noop */
  }
}

/**
 * 캐시 우선으로 적용 평가계획 스냅샷을 반환.
 * - 메모리/localStorage hit → DB 호출 없음
 * - miss → DB 조회 후 등록 페이지와 동일한 스냅샷 캐시에 기록
 */
export async function resolveWorkspaceEvaluationPlanApplied(
  workspaceId: string,
  gradeId: string,
  options?: { forceRefresh?: boolean }
): Promise<WorkspaceEvaluationPlanAppliedSnapshot[]> {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  if (!ws || !gid) return [];

  const memKey = appliedSnapshotsMemoryKey(ws, gid);
  if (!options?.forceRefresh) {
    const cached = loadWorkspaceEvaluationPlanAppliedSnapshots(ws, gid);
    if (cached.found) return cached.snapshots;
  }

  const existing = appliedSnapshotsInflight.get(memKey);
  if (existing && !options?.forceRefresh) return existing;

  const request = (async () => {
    const snapshots = await getWorkspaceEvaluationPlanApplied(ws, gid);
    saveWorkspaceEvaluationPlanAppliedSnapshots(ws, gid, snapshots);
    const entries: WorkspaceEvaluationPlanAppliedEntry[] = snapshots
      .map((snap) => {
        const planId = (snap.source_plan_id ?? '').trim();
        if (!planId) return null;
        return {
          planId,
          categoryName: snap.source_category_name,
          categoryScope: snap.source_category_scope,
          planScope: normalizePlanScope(snap.source_plan_scope, snap.source_category_scope),
        } satisfies WorkspaceEvaluationPlanAppliedEntry;
      })
      .filter((row): row is WorkspaceEvaluationPlanAppliedEntry => !!row);
    saveWorkspaceEvaluationPlanAppliedEntries(ws, gid, entries);
    return snapshots;
  })();

  appliedSnapshotsInflight.set(memKey, request);
  try {
    return await request;
  } finally {
    if (appliedSnapshotsInflight.get(memKey) === request) {
      appliedSnapshotsInflight.delete(memKey);
    }
  }
}
